import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@/lib/connect";
import { TrackingDomainService } from "@gen/tracker/v1/tracking_domain_connect";
import { TrackingDomain, TrackingDomainStatus, type DomainCampaign, type DomainPort, type TrackingDomainCounts } from "@gen/tracker/v1/tracking_domain_pb";
import { ListSort, Pagination } from "@gen/tracker/v1/common_pb";
import { useByIds } from "@/lib/services/catalog";

// A tracking domain is the tracker's BINDING of a domain to the click edge.
// Who manages the domain is the adapter of the org's `domains` port
// (domains.attach.v1 — manual DNS, or a module, by the org's Connection in
// console); `bindingRecords` are what the fqdn must carry and `manageUrl`
// points at the adapter's UI when it has one. This service only creates,
// syncs and removes bindings, and asks how the port attaches a new one.

const client = createClient(TrackingDomainService);

export interface ListTrackingDomainsParams {
  /** Part of the fqdn. */
  search?: string;
  ids?: string[];
  /** fqdn | status | binding_status | created_at; none = the default first, then newest. */
  sort?: ListSort;
  /** The list's chips: busy | free | problems; none = every domain. */
  usage?: "" | "busy" | "free" | "problems";
  /** One page; the API's default is 50 and its ceiling 500. */
  limit?: number;
  offset?: number;
}

export const trackingDomainKeys = {
  all: ["tracking_domains"] as const,
  list: (params: ListTrackingDomainsParams) => [...trackingDomainKeys.all, "list", params] as const,
  detail: (id: string) => [...trackingDomainKeys.all, "detail", id] as const,
  port: () => [...trackingDomainKeys.all, "port"] as const,
};

/**
 * How «Подключить домен» attaches a name for the caller now (GetDomainPort):
 * native (a module attaches it, with the caller's free domains) or manual
 * (the records to set by hand). A refusal (`adapter_not_wired`,
 * `connector_unavailable`) or Unavailable is the query's error.
 */
export function useDomainPort(enabled = true) {
  return useQuery({
    queryKey: trackingDomainKeys.port(),
    enabled,
    retry: false,
    queryFn: async (): Promise<DomainPort> => (await client.getDomainPort({})).port!,
  });
}

export function useListTrackingDomains(params: ListTrackingDomainsParams = {}, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: trackingDomainKeys.list(params),
    enabled: options?.enabled ?? true,
    queryFn: async (): Promise<{ items: TrackingDomain[]; total: number; counts: TrackingDomainCounts | undefined }> => {
      const res = await client.listTrackingDomains({
        search: params.search ?? "",
        ids: params.ids ?? [],
        sort: params.sort,
        usage: params.usage ?? "",
        pagination: new Pagination({ limit: params.limit ?? 50, offset: params.offset ?? 0 }),
      });
      return { items: res.items, total: res.pagination?.total ?? res.items.length, counts: res.counts };
    },
  });
}

/** The domains with these ids, by id. */
export function useTrackingDomainsByIds(ids: Iterable<string>, enabled = true) {
  return useByIds<TrackingDomain>(
    trackingDomainKeys.all,
    ids,
    async (chunk) => (await client.listTrackingDomains({ ids: chunk, pagination: new Pagination({ limit: chunk.length, offset: 0 }) })).items,
    enabled,
  );
}

export function useGetTrackingDomain(id: string | undefined) {
  return useQuery({
    queryKey: trackingDomainKeys.detail(id ?? ""),
    enabled: !!id,
    queryFn: async () => {
      if (!id) return undefined;
      const res = await client.getTrackingDomain({ id });
      return res.domain;
    },
  });
}

/** The campaigns whose ad link lives on a domain (доска DomainDrawer), by name. */
export function useDomainCampaigns(id: string | undefined) {
  return useQuery({
    queryKey: [...trackingDomainKeys.all, "campaigns", id ?? ""] as const,
    enabled: !!id,
    queryFn: async (): Promise<DomainCampaign[]> => (await client.getTrackingDomain({ id: id! })).campaigns,
  });
}

export function useCreateTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { fqdn: string; isDefault?: boolean }) => {
      const res = await client.createTrackingDomain({ fqdn: input.fqdn, isDefault: input.isDefault ?? false });
      return res.domain!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trackingDomainKeys.all });
    },
  });
}

export function useUpdateTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; status?: TrackingDomainStatus }) => {
      const res = await client.updateTrackingDomain({ id: input.id, status: input.status });
      return res.domain!;
    },
    onSuccess: (domain) => {
      void qc.invalidateQueries({ queryKey: trackingDomainKeys.all });
      qc.setQueryData(trackingDomainKeys.detail(domain.id), domain);
    },
  });
}

export function useDeleteTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await client.deleteTrackingDomain({ id });
      return id;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trackingDomainKeys.all });
    },
  });
}

export function useSetDefaultTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.setDefaultTrackingDomain({ id });
      return res.domain!;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: trackingDomainKeys.all });
    },
  });
}

// useSyncTrackingDomain re-reads the attachment from the port's adapter now
// instead of waiting for the minute sync.
export function useSyncTrackingDomain() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.syncTrackingDomain({ id });
      return res.domain!;
    },
    onSuccess: (domain) => {
      qc.setQueryData(trackingDomainKeys.detail(domain.id), domain);
      void qc.invalidateQueries({ queryKey: trackingDomainKeys.all });
    },
  });
}
